import Cookie from "../utils/client/Cookie";
import { NEW_VIDEO_LAYOUT_SERIES_IDS } from "../utils/quicktake/Constants";

export const PersonalizationActionTypes = {
    HANDLE_PERSONALIZATION_RESPONSE: "HANDLE_PERSONALIZATION_RESPONSE",
    PERSONALIZATION_ERROR: "PERSONALIZATION_ERROR",
};

const DEFAULT_RECOMMENDER = "cfru";
const EXP_PREF = "exp_pref";

const DEFAULT_QUERY = {
    backfillFacets: "Video|All",
    decayCoefficient: 1,
    inactivePeriod: 2592000000,
    limit: 15,
    maxAge: 604800000,
    rescorers: "random;tpx",
    "rescorers.tpx.minimumScore": 0.1,
    "rescorers.random.minimumScore": 0.1,
    numResourcesToRearrange: 100,
    resourceTypes: "Video",
    includedSites: "bbiz;pursuits;technology;markets;bpol",
    "niMaxAge.USSTOCKS": 43200000,
    "niMaxAge.FIVE": 14400000,
};
const EXCLUDE_QUICKTAKE_QUERY = {
    "filters.exclude.Series": Array.from(NEW_VIDEO_LAYOUT_SERIES_IDS).join(";"),
};

const SEARCH_REFERRAL_OVERRIDE = {
    query: {
        maxAge: 1209600000,
        rescorers: "tpx",
    },
    recommender: "rr",
};

const SEARCH = "SEARCH";

const LIVE_OVERWRITES = {
    interactionsLimit: 25,
};

const dispatchSuccess = (dispatch) => (recommendations) =>
    dispatch({
        type: PersonalizationActionTypes.HANDLE_PERSONALIZATION_RESPONSE,
        data: {
            videoList: recommendations,
        },
    });

const dispatchError = (dispatch) => (error) => {
    console.warn("Personalization error", error);
    dispatch({
        type: PersonalizationActionTypes.PERSONALIZATION_ERROR,
        error,
    });
};

const appendRecommendationsToCurrentList = (videoList) => (recommendations) => [
    ...videoList,
    ...recommendations,
];

const addCurrentVideo =
    (currentVideo, isLandingPageVideo) => (recommendations) => {
        if (isLandingPageVideo) {
            return recommendations;
        }
        const inList = recommendations.some(
            (item) => item.video.bmmrId === currentVideo.video.bmmrId
        );
        return inList ? recommendations : [currentVideo, ...recommendations];
    };

async function getRecommendations({
    url,
    search: params,
    recommender = DEFAULT_RECOMMENDER,
}) {
    /*
     * This is client side only library which throws error if included on server
     * */
    const PersonalizationClient = (await import("ms.personalization")).default;
    const expPref = Cookie.get(EXP_PREF);

    return new PersonalizationClient({ url })
        .getRecommended(recommender, {
            ...(expPref && { EXP_PREF: expPref }),
            ...params,
        })
        .then(formatRecommendations);
}

function formatRecommendations(recommendations) {
    return recommendations.map((item) => ({
        headline: {
            html: item.headline,
        },
        url: item.url,
        video: {
            duration: item.duration,
            bmmrId: item.assetID,
            thumbnail: {
                url: item.thumbnailUrl,
            },
        },
    }));
}

export const loadUserRecommendations = () => async (dispatch, getState) => {
    const {
        clientConfig: { personalizationUrl: url },
        video: { videoStory },
    } = getState();

    const {
        video: { bmmrId },
        isLandingPageVideo = false,
    } = videoStory;

    const exclude = `Video|${bmmrId}`;

    let domainCategory = "";
    try {
        const abbaClient = await window.__bloomberg__.getAbbaClient();
        const contextMetadata = await abbaClient.getContextMetadata();
        domainCategory = contextMetadata.metadata?.attribute?.domainCategory;
    } catch (e) {
        console.warn("Unable to obtain domainCategory from abbaClient - ", e);
    }

    const query = {
        ...DEFAULT_QUERY,
        ...EXCLUDE_QUICKTAKE_QUERY,
        ...(domainCategory === SEARCH ? SEARCH_REFERRAL_OVERRIDE.query : {}),
    };

    return getRecommendations({
        url,
        recommender:
            domainCategory === SEARCH
                ? SEARCH_REFERRAL_OVERRIDE.recommender
                : DEFAULT_RECOMMENDER,
        search: {
            ...query,
            exclude,
            currentResource: exclude,
        },
    })
        .then(addCurrentVideo(videoStory, isLandingPageVideo))
        .then(dispatchSuccess(dispatch))
        .catch(dispatchError(dispatch));
};

export const loadUserRecommendationsForLive = () => (dispatch, getState) => {
    const {
        clientConfig: { personalizationUrl: url },
    } = getState();

    return getRecommendations({
        url,
        search: {
            ...DEFAULT_QUERY,
            ...LIVE_OVERWRITES,
            ...EXCLUDE_QUICKTAKE_QUERY,
        },
    })
        .then(dispatchSuccess(dispatch))
        .catch(dispatchError(dispatch));
};

export const loadMoreUserRecommendations = () => (dispatch, getState) => {
    const {
        clientConfig: { personalizationUrl: url },
        personalization: { videoList },
        video: { videoStory: { video: { bmmrId } = {} } = {} } = {},
    } = getState();

    const search = bmmrId
        ? { currentResource: `Video|${bmmrId}` }
        : LIVE_OVERWRITES;

    return getRecommendations({
        url,
        search: {
            ...DEFAULT_QUERY,
            exclude: getPersonalizeExcludedIds(videoList),
            ...search,
            ...EXCLUDE_QUICKTAKE_QUERY,
        },
    })
        .then(appendRecommendationsToCurrentList(videoList))
        .then(dispatchSuccess(dispatch))
        .catch(dispatchError(dispatch));
};

function getPersonalizeExcludedIds(videoList) {
    const excludedList = videoList.reduce((result, videoStory, index) => {
        result += `Video|${videoStory.video.bmmrId}`;

        if (index !== videoList.length - 1) {
            result += ";";
        }
        return result;
    }, "");

    return excludedList;
}
